import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wallet, TrendingUp, ArrowUpRight, Loader2, AlertTriangle,
  CheckCircle2, Clock, XCircle, Send,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase 24e · Creator earnings page.
 * Route: /account/creator/earnings
 * Shows balance, lifetime, payout history + withdrawal form.
 */
export default function CreatorEarnings() {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wdAmount, setWdAmount] = useState("2000");
  const [wdAcct, setWdAcct] = useState("");
  const [wdBusy, setWdBusy] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [e, p, a] = await Promise.all([
        apiFetch("/marketplace/creator/me/earnings"),
        apiFetch("/marketplace/creator/me/payouts"),
        apiFetch("/marketplace/creator/me/assets?limit=100"),
      ]);
      setEarnings(e);
      setPayouts(p?.items || []);
      setAssets(a?.items || []);
    } catch (e) {
      toast.error(`Errore caricamento: ${String(e?.message || e)}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function handleWithdraw() {
    const cents = parseInt(wdAmount, 10);
    if (!cents || cents < 2000) {
      toast.error("Minimo $20 (2000 cent)");
      return;
    }
    setWdBusy(true);
    try {
      await apiFetch("/marketplace/creator/me/withdrawal", {
        method: "POST",
        body: JSON.stringify({
          amount_cents: cents,
          stripe_connect_account_id: wdAcct || null,
        }),
      });
      toast.success("Richiesta di prelievo creata");
      setWdAmount("2000");
      setWdAcct("");
      await loadAll();
    } catch (e) {
      toast.error(`Prelievo fallito: ${String(e?.message || e)}`);
    } finally {
      setWdBusy(false);
    }
  }

  const eur = (c) => (Number(c || 0) / 100).toFixed(2).replace(".", ",");

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 max-w-6xl mx-auto w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-8">
              <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                Phase 24e · Creator Revenue
              </div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="creator-earnings-title">
                Earnings & Payouts
              </h1>
              <p className="text-sm text-text-secondary mt-2">
                Revenue split 70% Creator / 30% Platform · Payout min $20 · Weekly cron Mon 06:00 UTC (mock)
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Phase 25b · Revenue Tier widget */}
                <RevenueTierCard />

                {/* KPI cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <KpiCard
                    icon={Wallet}
                    label="Balance"
                    value={`$${eur(earnings?.balance_cents)}`}
                    tone="success"
                    hint={earnings?.frozen ? "⚠ Balance frozen (ban)" : "Disponibile per prelievo"}
                    testid="kpi-balance"
                  />
                  <KpiCard
                    icon={TrendingUp}
                    label="Sales gross"
                    value={`$${eur(earnings?.sales_gross_cents)}`}
                    tone="primary"
                    hint={`${earnings?.sales_count || 0} vendite totali`}
                    testid="kpi-sales-gross"
                  />
                  <KpiCard
                    icon={ArrowUpRight}
                    label="Paid Out"
                    value={`$${eur(earnings?.lifetime_paid_out_cents)}`}
                    tone="secondary"
                    hint="Lifetime paid"
                    testid="kpi-paid-out"
                  />
                  <KpiCard
                    icon={AlertTriangle}
                    label="Refunds"
                    value={`$${eur(earnings?.lifetime_refunds_cents)}`}
                    tone="alert"
                    hint="Rimborsi totali"
                    testid="kpi-refunds"
                  />
                </div>

                {/* Withdrawal form */}
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Send className="h-4 w-4 text-[var(--color-primary)]" />
                    <h2 className="text-sm font-semibold uppercase tracking-widest">
                      Request Withdrawal
                    </h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                        Amount (cents)
                      </label>
                      <input
                        data-testid="withdrawal-amount"
                        type="number"
                        min="2000"
                        value={wdAmount}
                        onChange={(e) => setWdAmount(e.target.value)}
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:outline-none"
                      />
                      <div className="text-[10px] text-text-secondary mt-1">
                        Min $20 · Current: ${eur(wdAmount)}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                        Stripe Connect Acct (mock)
                      </label>
                      <input
                        data-testid="withdrawal-acct"
                        type="text"
                        placeholder="acct_mock_..."
                        value={wdAcct}
                        onChange={(e) => setWdAcct(e.target.value)}
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:outline-none"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        data-testid="withdrawal-submit"
                        onClick={handleWithdraw}
                        disabled={wdBusy || earnings?.frozen}
                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs uppercase tracking-widest ${
                          wdBusy || earnings?.frozen
                            ? "bg-white/5 text-text-secondary cursor-not-allowed"
                            : "bg-white hover:bg-[#EDEDED] text-black hover:shadow-lg hover:shadow-[var(--color-primary)]/30"
                        }`}
                      >
                        {wdBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Richiedi Prelievo
                      </button>
                    </div>
                  </div>
                </div>

                {/* Payout history */}
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                  <h2 className="text-sm font-semibold uppercase tracking-widest mb-4">
                    Payout History
                  </h2>
                  {payouts.length === 0 ? (
                    <p className="text-xs text-text-secondary">Nessun prelievo effettuato.</p>
                  ) : (
                    <div className="space-y-2">
                      {payouts.map((p) => (
                        <div
                          key={p.id}
                          data-testid={`payout-row-${p.id}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg)] border border-white/5"
                        >
                          <div className="flex items-center gap-3">
                            <PayoutStatusIcon status={p.status} />
                            <div>
                              <div className="text-sm font-semibold tabular-nums">${eur(p.amount_cents)}</div>
                              <div className="text-[10px] text-text-secondary">
                                {new Date(p.requested_at).toLocaleString("it-IT")}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">
                              {p.status}
                            </div>
                            {p.stripe_transfer_id && (
                              <div className="text-[9px] text-text-secondary font-mono">
                                {p.stripe_transfer_id.slice(0, 24)}…
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Own assets */}
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                  <h2 className="text-sm font-semibold uppercase tracking-widest mb-4">
                    My Paid Assets ({assets.length})
                  </h2>
                  {assets.length === 0 ? (
                    <p className="text-xs text-text-secondary">Non hai ancora pubblicato asset a pagamento.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-3">
                      {assets.map((a) => (
                        <div
                          key={a.asset_id}
                          data-testid={`own-asset-${a.asset_id}`}
                          className="p-3 rounded-lg bg-[var(--color-bg)] border border-white/5"
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div className="text-sm font-semibold line-clamp-1">{a.title}</div>
                            <div className="text-[10px] uppercase tracking-widest text-text-secondary shrink-0 ml-2">
                              {a.status}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-text-secondary">
                            <span>${eur(a.price_eur_cents)}</span>
                            <span>{a.sales_count || 0} vendite</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary", hint, testid }) {
  const toneCls = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    success: "text-[var(--color-success)]",
    alert: "text-[var(--color-alert)]",
  }[tone];
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
        <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
    </div>
  );
}

function PayoutStatusIcon({ status }) {
  if (status === "paid") return <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />;
  if (status === "pending") return <Clock className="h-4 w-4 text-neutral-400" />;
  if (status === "rejected") return <XCircle className="h-4 w-4 text-[var(--color-alert)]" />;
  return <Clock className="h-4 w-4 text-text-secondary" />;
}

// Phase 25b · Revenue Tier widget — reads user's active subscription
// and shows the applicable split. Non-premium users see the upgrade CTA.
function RevenueTierCard() {
  const [tier, setTier] = React.useState(null); // 'base' | 'premium' | 'enterprise'
  React.useEffect(() => {
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${API}/api/subscriptions/me`, { credentials: "include" });
        if (!r.ok) { setTier("base"); return; }
        const d = await r.json();
        const tiers = (d?.items || d?.subscriptions || []).map((s) => s.tier_slug);
        if (tiers.includes("premium_creator_enterprise")) setTier("enterprise");
        else if (tiers.includes("premium_creator")) setTier("premium");
        else setTier("base");
      } catch {
        setTier("base");
      }
    })();
  }, []);
  if (!tier) return null;
  const config = {
    base: {
      label: "Base",
      split: "70/30",
      color: "text-text-secondary",
      border: "border-[var(--color-border)]",
      cta: true,
    },
    premium: {
      label: "Premium Creator",
      split: "80/20",
      color: "text-[var(--color-primary)]",
      border: "border-[var(--color-primary)]/40",
      cta: false,
    },
    enterprise: {
      label: "Enterprise",
      split: "90/10",
      color: "text-[var(--color-success)]",
      border: "border-[var(--color-success)]/40",
      cta: false,
    },
  }[tier];
  return (
    <div
      data-testid="revenue-tier-card"
      className={`rounded-2xl border ${config.border} bg-[var(--color-card)] p-4 flex items-center justify-between gap-4`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">
          Revenue tier · at-sale split
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="revenue-tier-label" className={`text-lg font-semibold ${config.color}`}>
            {config.label}
          </span>
          <span data-testid="revenue-tier-split" className="text-xs text-text-secondary">
            {config.split} creator/platform
          </span>
        </div>
      </div>
      {config.cta && (
        <a
          href="/pricing?src=creator_earnings_tier"
          data-testid="revenue-tier-upgrade-cta"
          className="text-[10px] uppercase tracking-widest px-4 py-2 rounded-full bg-white hover:bg-[#EDEDED] text-black font-semibold hover:opacity-90"
        >
          Upgrade to Premium · $29/mo → 80/20
        </a>
      )}
    </div>
  );
}
